import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useUser } from "../lib/customHooks"
import { restartAllBots, startAllBots, stopAllBots } from "../services/botApis"
import getTimeDiffInDays from "../utils/getTimeDiffInDays"
import getTimeDiffFaString from "../utils/getTimeDiffFaString"
import { APP_ROUTES } from "../utils/constants"
import actionHelper from "../lib/actionHelper"
import { toast } from "react-toastify"
import MusicBotMoreModal from "../components/MusicBots/MusicBotMoreModal"
import CreateMusicBotModal from "../components/MusicBots/CreateMusicBotModal"
import Sidebar from "../components/Sidebar"
import Table from "../components/Table"
import AutoRenewButton from "../components/RankSystems/AutoRenewButton"
import { getRankSystemList } from "../services/ranksystemApis"
import RankSystemMoreModal from "../components/RankSystems/RankSystemMoreModal"
import CreateRankSystemModal from "../components/RankSystems/CreateRankSystemModal"
import { useAuth } from "../lib/useAuth"
import { useSocket, useSocketEvent } from "../contexts/SocketContext"
import { EyeOutlined } from "@ant-design/icons"
import sidebarStore from "../store/sidebarStore"

const cols = [
    {
        name: "حالت",
        colSpan: 1,
    },
    {
        name: "وضعیت",
        colSpan: 1,
    },
    {
        name: "اسم تمپلیت",
        colSpan: 1,
    },

    {
        name: "نام کاربری",
        colSpan: 1,
    },
    {
        name: "پسورد",
        colSpan: 1,
    },
    {
        name: "تاریخ انقضا",
        colSpan: 1,
    },
    {
        name: "تمدید اتومات",
        colSpan: 1,
    },

    {
        name: "تنظیمات",
        colSpan: 1,
    },
]

const RankSystems = () => {
    const { user, loading } = useAuth()
    const { isConnected, sendMessage } = useSocket()
    const companyDomainName = useRef()

    const navigate = useNavigate()

    const [ranksystemList, setRanksystemList] = useState([])
    const [rows, setRows] = useState([])
    const [selecteds, setSelecteds] = useState([])
    const [createIsOpen, setCreateIsOpen] = useState(false)
    const [searchBar, setSearchBar] = useState("")
    const setSection = sidebarStore((state) => state.setSection)

    const searchBarChangeHandler = async (e) => {
        await new Promise((resolve) => setTimeout(resolve, 500))
        setSearchBar(e.target.value)
    }

    useEffect(() => {
        if (user) {
            companyDomainName.current = user?.companyDomainName
        }
    }, [loading])

    const toggleCreate = () => {
        setCreateIsOpen(!createIsOpen)
    }

    const createRows = (ranksystems) => {
        return ranksystems?.map((rs) => {
            const now = new Date()
            const createdDate = new Date(rs.created)
            const expireDate = new Date(rs.expires)
            const expires = rs.expires == null ? null : getTimeDiffInDays(expireDate, now)
            rs.expString = rs.expires == null ? null : getTimeDiffFaString(expireDate, now)
            rs.crtString = getTimeDiffFaString(createdDate, now)

            let obj = {
                id: rs.id,
                state: (
                    <span className={rs.state == "active" ? "bg-blur-success" : "bg-blur-warning"}>
                        {rs.state == "active" ? "فعال" : "غیرفعال"}
                    </span>
                ),
                status: (
                    <span className={rs.status == "online" ? "bg-blur-success" : "bg-blur-danger"}>
                        {rs.status == "online" ? "روشن" : "خاموش"}
                    </span>
                ),
                templateName: rs.template_name,

                username: rs.username,
                password: rs.password,
                expString:
                    expires !== null ? (
                        <span className={expires >= 0 ? "text-success" : "text-danger"}>{rs.expString}</span>
                    ) : (
                        <span>دائمی</span>
                    ),
                renew: <AutoRenewButton ranksystem={rs} />,
                more: (
                    <div className="flex gap-2">
                        <RankSystemMoreModal ranksystem={rs} />
                        <a
                            href={`https://ranksystem.${companyDomainName.current}/${rs.template_name}/webinterface`}
                            target="_blank"
                        >
                            <EyeOutlined style={{ fontSize: "20px" }} />
                        </a>
                    </div>
                ),
            }
            return obj
        })
    }

    const handleBotList = useCallback((data) => {
        console.log(data)
        setRanksystemList(data)
    }, [])

    const handleBotAdded = useCallback((newBot) => {
        setRanksystemList((list) => [...list, newBot])
    }, [])

    const handleBotUpdated = useCallback((newData) => {
        setRanksystemList((oldBotList) => {
            return oldBotList.map((s) => {
                return s.id == newData.id ? Object.assign(s, newData) : s
            })
        })
    }, [])

    const handleBotDeleted = useCallback((deletedId) => {
        setRanksystemList((oldBotList) => {
            return oldBotList.filter((s) => s.id != deletedId)
        })
    }, [])

    useSocketEvent("ranksystemList", handleBotList)
    useSocketEvent("ranksystemAdded", handleBotAdded)
    useSocketEvent("ranksystemUpdated", handleBotUpdated)
    useSocketEvent("ranksystemDeleted", handleBotDeleted)

    const handleRefresh = () => {
        sendMessage("getRanksystemList")
    }

    useEffect(() => {
        if (!isConnected) {
            return
        }

        if (user && user.can_use_ranksystems) {
            sendMessage("getRanksystemList")
        } else if (user && user.can_use_servers) {
            navigate(APP_ROUTES.INDEX)
        } else if (user && user.can_use_manager_bots) {
            navigate(APP_ROUTES.TS_MANAGER_BOTS)
        } else if (user && user.can_use_bot) {
            navigate(APP_ROUTES.MUSIC_BOTS)
        } else if (user) {
            toast.error("شما هیچگونه دسترسی ندارید")
            localStorage.clear()
            navigate(APP_ROUTES.SIGN_IN)
        } else {
            return
        }
    }, [isConnected])

    useEffect(() => {
        if (searchBar && searchBar !== "" && ranksystemList) {
            let searchedRows = ranksystemList.filter((bot) => {
                if (
                    !bot.username?.toString().includes(searchBar) &&
                    !bot.template_name?.toString().includes(searchBar) &&
                    !bot.author?.toString().includes(searchBar)
                ) {
                    return false
                } else {
                    return true
                }
            })
            setRows(createRows(searchedRows))
        } else if (ranksystemList) {
            setRows(createRows(ranksystemList))
        }
    }, [searchBar, ranksystemList])

    const multiTasking = {
        setSelecteds: setSelecteds,
        selecteds: selecteds,
        tasks: [
            {
                title: "روشن کردن",
                color: "success",
                icon: "play",
                onClick: () => {
                    if (selecteds.length != 0) {
                        actionHelper({
                            confirmation: {
                                label: "ایا مطمئن هستید بات های انتخاب شده را روشن کنید؟",
                                actionLabel: "روشن کردن",
                                color: "success",
                            },
                            callback: () => {
                                return startAllBots(user.token, {
                                    selecteds: selecteds.map((s) => s.templateName.toString()),
                                })
                            },
                            texts: { success: "استارت شد", pending: "درحال استارت" },
                        })
                    } else {
                        toast.error("باتی انتخاب نشده است")
                    }
                },
            },
            {
                title: "خاموش کردن",
                color: "danger",
                icon: "power-off",
                onClick: () => {
                    if (selecteds.length != 0) {
                        actionHelper({
                            confirmation: {
                                label: "آیا مطمئن هستید بات های انتخاب شده را خاموش کنید؟",
                                actionLabel: "خاموش کردن",
                                color: "danger",
                            },
                            callback: () => {
                                return stopAllBots(user.token, {
                                    selecteds: selecteds.map((s) => s.templateName.toString()),
                                })
                            },
                            texts: { success: "خاموش شد", pending: "درحال خاموش" },
                        })
                    } else {
                        toast.error("باتی انتخاب نشده است")
                    }
                },
            },
            {
                title: "ریستارت کردن",
                color: "border",
                icon: "repeat",
                onClick: () => {
                    if (selecteds.length != 0) {
                        actionHelper({
                            confirmation: {
                                label: "ایا مطمئن هستید بات های انتخاب شده را ریستارت کنید؟",
                                actionLabel: "ریستارت کردن",
                                color: "danger",
                            },
                            callback: () => {
                                return restartAllBots(user.token, {
                                    selecteds: selecteds.map((s) => s.templateName.toString()),
                                })
                            },
                            texts: { success: "ریستارت شد", pending: "درحال ریستارت" },
                        })
                    } else {
                        toast.error("باتی انتخاب نشده است")
                    }
                },
            },
        ],
    }

    const section = {
        title: "بات های رنک سیستم",
        setSearchBar: searchBarChangeHandler,
        buttons: [
            {
                name: "ساخت بات جدید",
                onClick: toggleCreate,
                color: "border",
                iconName: "square-plus",
            },
            {
                name: "روشن کردن همه",
                onClick: () =>
                    actionHelper({
                        confirmation: {
                            label: "ایا مطمئن هستید همه ی بات ها را روشن کنید؟",
                            actionLabel: "روشن کردن",
                            color: "success",
                        },
                        callback: () => {
                            return startAllBots(user.token)
                        },
                        texts: { success: "روشن شد", pending: "درحال روشن کردن" },
                    }),
                color: "success",
                iconName: "play",
            },
            {
                name: "ریستارت همه",
                onClick: () =>
                    actionHelper({
                        confirmation: {
                            label: "ایا مطمئن هستید همه ی بات ها را ریستارت کنید؟",
                            actionLabel: "ریستارت کردن",
                            color: "warning",
                        },
                        callback: () => {
                            return restartAllBots(user.token)
                        },
                        texts: { success: "ریستارت شد", pending: "درحال ریستارت" },
                    }),
                color: "warning",
                iconName: "repeat",
            },
            {
                name: "خاموش کردن همه",
                onClick: () =>
                    actionHelper({
                        confirmation: {
                            label: "ایا مطمئن هستید همه ی بات ها را خاموش کنید؟",
                            actionLabel: "خاموش کردن",
                            color: "danger",
                        },
                        callback: () => {
                            return stopAllBots(user.token)
                        },
                        texts: { success: "خاموش شد", pending: "درحال خاموش" },
                    }),
                color: "danger",
                iconName: "power-off",
            },
        ],
    }

    if (!user) {
        return "Loading..."
    }

    setSection(section)

    return (
        <>
            <CreateRankSystemModal user={user} isOpen={createIsOpen} toggleModal={toggleCreate} />
            <Table
                cols={cols}
                rows={rows}
                paging={{ maxRows: 10 }}
                multiTasking={multiTasking}
                handleRefresh={handleRefresh}
            />
        </>
    )
}

export default RankSystems
